import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { isElectronApp } from "../utils/utils";

/** @returns {{ startRecorder?: Function, stopRecorder?: Function, listRecordings?: Function, deleteRecording?: Function }} */
function electronAPI() {
  if (typeof window === "undefined" || !window.electronAPI) return {};
  return window.electronAPI;
}

export default function ScreenRecorderPage() {
  const desktop = isElectronApp();

  const [url, setUrl] = useState("https://example.com");
  const [pageName, setPageName] = useState("Page 1");
  const [browser, setBrowser] = useState("chromium");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(null);
  const [savedList, setSavedList] = useState([]);

  const refreshSavedListDesktop = useCallback(async () => {
    if (!desktop) return;
    const api = electronAPI();
    if (!api.listRecordings) return;
    try {
      const res = await api.listRecordings();
      if (res?.ok) setSavedList(res.recordings || []);
    } catch (e) {
      console.warn("listRecordings", e);
    }
  }, [desktop]);

  useEffect(() => {
    refreshSavedListDesktop();
  }, [refreshSavedListDesktop]);

  const canStart = useMemo(() => {
    if (!desktop) return false;
    return Boolean(url.trim() && pageName.trim()) && !busy && !sessionId;
  }, [desktop, url, pageName, busy, sessionId]);

  const handleStart = useCallback(async () => {
    if (!canStart) return;
    setBusy(true);
    try {
      const api = electronAPI();
      const result = await api.startRecorder({
        url: url.trim(),
        browser,
        pageName: pageName.trim(),
      });
      if (!result?.ok) {
        throw new Error(result?.message || "Failed to start");
      }
      setSessionId(result.recordingId || result.session_id);
      setRecording(null);
      toast.success(
        "Recording started — use the Playwright browser window on your desktop.",
      );
    } catch (e) {
      toast.error(e.message || "Failed to start recording");
    } finally {
      setBusy(false);
    }
  }, [canStart, url, browser, pageName]);

  const handleStop = useCallback(async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const api = electronAPI();
      const result = await api.stopRecorder(sessionId);
      if (!result?.ok) {
        throw new Error(result?.message || "Failed to stop");
      }
      setRecording(result.recording);
      setSessionId(null);
      await refreshSavedListDesktop();
      const r = result.recording;
      toast.success(
        `Stopped — ${r?.actions?.length ?? 0} actions · ${r?.videoArtifact || "no video"} (saved locally)`,
      );
    } catch (e) {
      toast.error(e.message || "Failed to stop recording");
    } finally {
      setBusy(false);
    }
  }, [sessionId, refreshSavedListDesktop]);

  const handleDeleteSaved = useCallback(
    async (id) => {
      if (!desktop || !window.confirm("Delete this saved recording from disk?")) return;
      const api = electronAPI();
      try {
        const res = await api.deleteRecording(id);
        if (!res?.ok) throw new Error(res?.message || "Delete failed");
        await refreshSavedListDesktop();
        if (recording?.id === id) setRecording(null);
        toast.success("Deleted");
      } catch (e) {
        toast.error(e.message || "Delete failed");
      }
    },
    [desktop, refreshSavedListDesktop, recording],
  );

  const downloadJson = useCallback(() => {
    if (!recording) return;
    const blob = new Blob([JSON.stringify(recording, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${recording.id || "recording"}.recording.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [recording]);

  const copyText = useCallback((text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied"),
      () => toast.error("Could not copy"),
    );
  }, []);

  const modeExplanation = desktop ? (
    <p className="mt-1 max-w-3xl text-sm text-[var(--color-text-secondary)]">
      <strong className="font-medium text-[var(--color-text-primary)]">
        Desktop mode:
      </strong>{" "}
      Playwright opens a headed browser on this machine (same pattern as the packaged QMS
      app). Interact there, then stop — JSON and WebM save under app user data (
      <code className="text-xs">screen-recordings</code>).
    </p>
  ) : (
    <p className="mt-1 max-w-3xl text-sm text-[var(--color-text-secondary)]">
      In the browser build, recording is not available — open the packaged TestNimbus /
      Electron app (or{" "}
      <code className="text-xs">npm run electron</code> during development). Recording
      runs locally via Playwright; there is no server API for this.
    </p>
  );

  const artifactCaption =
    desktop ? "Local artifact folder (this device)" : "";

  return (
    <div className="flex min-h-full flex-col gap-6 p-6 md:p-8">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
          Screen recorder
        </h1>
        {modeExplanation}
        {desktop ? (
          <p className="mt-2 text-xs text-[var(--color-text-tertiary)]">
            Recordings stay on this machine; no backend session is involved.
          </p>
        ) : null}
      </div>

      <div className="max-w-xl space-y-4 rounded-lg border border-[var(--color-border-primary)] bg-[var(--color-bg-primary)] p-5 shadow-sm">
        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">
            Start URL
          </label>
          <input
            className="w-full rounded-md border border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] px-3 py-2 text-sm"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={Boolean(sessionId) || !desktop}
            type="url"
            placeholder="https://…"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">
            Page name
          </label>
          <input
            className="w-full rounded-md border border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] px-3 py-2 text-sm"
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            disabled={Boolean(sessionId) || !desktop}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">
            Browser
          </label>
          <select
            className="w-full rounded-md border border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] px-3 py-2 text-sm"
            value={browser}
            onChange={(e) => setBrowser(e.target.value)}
            disabled={Boolean(sessionId) || !desktop}
          >
            <option value="chromium">Chromium</option>
            <option value="firefox">Firefox</option>
            <option value="webkit">WebKit</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="button"
            disabled={!canStart}
            onClick={handleStart}
            className="inline-flex items-center justify-center rounded-md bg-[var(--color-primary-600)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy && !sessionId ? "Starting…" : "Start recording"}
          </button>
          <button
            type="button"
            disabled={!desktop || !sessionId || busy}
            onClick={handleStop}
            className="inline-flex items-center justify-center rounded-md border border-red-600 bg-transparent px-4 py-2 text-sm font-medium text-red-600 disabled:opacity-50"
          >
            {busy && sessionId ? "Stopping…" : "Stop & save"}
          </button>
        </div>

        {!desktop ? (
          <p className="text-sm text-[var(--color-text-secondary)]">
            Start recording is disabled until you open this page in the desktop app.
          </p>
        ) : sessionId ? (
          <p className="text-xs text-green-700">
            Active session:{" "}
            <code className="break-all rounded bg-[var(--color-bg-hover)] px-1">
              {sessionId}
            </code>
          </p>
        ) : null}
      </div>

      {desktop && savedList.length > 0 ? (
        <div className="space-y-2">
          <h2 className="text-lg font-medium text-[var(--color-text-primary)]">
            Saved on this computer
          </h2>
          <ul className="space-y-2">
            {savedList.map((r) => (
              <li
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-[var(--color-border-primary)] bg-[var(--color-bg-primary)] px-3 py-2 text-sm"
              >
                <span>
                  {r.name || r.id}{" "}
                  <span className="text-[var(--color-text-tertiary)]">
                    · {r.actionsCount ?? 0} actions
                  </span>
                </span>
                <span className="flex gap-2">
                  <button
                    type="button"
                    className="text-[var(--color-primary-600)]"
                    onClick={async () => {
                      const api = electronAPI();
                      const gr = await api.getRecording(r.id);
                      if (gr?.ok) setRecording(gr.recording);
                      else toast.error(gr?.message || "Load failed");
                    }}
                  >
                    Load
                  </button>
                  <button
                    type="button"
                    className="text-red-600"
                    onClick={() => handleDeleteSaved(r.id)}
                  >
                    Delete
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {recording ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-medium text-[var(--color-text-primary)]">
              Last loaded result
            </h2>
            <button
              type="button"
              onClick={downloadJson}
              className="rounded-md border border-[var(--color-border-primary)] px-3 py-1 text-xs"
            >
              Download JSON
            </button>
          </div>
          <p className="text-xs text-[var(--color-text-secondary)]">
            {desktop ? `${artifactCaption}: ` : null}
            {recording.videoArtifact ||
              recording.videoPath ||
              "none"}{" "}
            · JSON:{" "}
            {recording.recordingJsonArtifact || "downloaded blob / see folder"}
          </p>
          {recording.localArtifactDir ? (
            <p className="text-xs font-mono text-[var(--color-text-tertiary)]">
              {recording.localArtifactDir}
            </p>
          ) : null}

          <div className="overflow-x-auto rounded-lg border border-[var(--color-border-primary)]">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-[var(--color-bg-hover)]">
                <tr>
                  <th className="px-2 py-2">#</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Name</th>
                  <th className="px-2 py-2">Locator (css)</th>
                  <th className="px-2 py-2">Copy</th>
                </tr>
              </thead>
              <tbody>
                {(recording.actions || []).map((a) => (
                  <tr
                    key={`${a.index}-${a.timestamp}`}
                    className="border-t border-[var(--color-border-primary)]"
                  >
                    <td className="px-2 py-2 align-top">{a.index}</td>
                    <td className="px-2 py-2 align-top">{a.type}</td>
                    <td className="px-2 py-2 align-top">{a.name}</td>
                    <td className="max-w-xs break-all px-2 py-2 align-top font-mono">
                      {a.locators?.css || a.locators?.id || "—"}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <button
                        type="button"
                        className="text-[var(--color-primary-600)]"
                        onClick={() =>
                          copyText(
                            a.locators?.fullXpath ||
                              a.locators?.css ||
                              a.locators?.xpath ||
                              "",
                          )
                        }
                      >
                        XPath/CSS
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <details className="rounded border border-[var(--color-border-primary)] p-2">
            <summary className="cursor-pointer text-sm">Raw JSON</summary>
            <pre className="mt-2 max-h-80 overflow-auto text-[10px]">
              {JSON.stringify(recording, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}
    </div>
  );
}
